import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { JSONContent } from '@tiptap/core';
import { useRef, useState } from 'react';

import { ImageUpload } from '@/components/form/ImageUpload';
import { RichTextField, type RichTextImages } from '@/components/form/RichTextField';
import { COVER_MAX_BYTES, LOGO_MAX_BYTES } from '@/lib/images';

import type { Arc } from './arcTypes';
import { canonical } from './draftEdits';
import type { PendingImage } from './draftEdits';
import { FormDialog } from './FormDialog';
import type { ArcWrites } from './useArcMutations';

type ImageKind = 'logo' | 'cover';

/** What the dialog changed; a part left undefined was not touched. */
export interface HeaderChanges {
  intro?: { content: JSONContent | null; images: Record<string, string> };
  /** A new image, or null to clear the ARC's own. */
  logo?: PendingImage | null;
  cover?: PendingImage | null;
}

interface EditHeaderDialogProps {
  arc: Arc;
  uploadImage: ArcWrites['uploadImage'];
  /** Puts the changes in the working copy; Save Draft saves them. */
  onApply: (changes: HeaderChanges) => void;
  onClose: () => void;
}

/**
 * What the app shows at the top of the ARC: its own logo and cover image,
 * and the introductory text.
 *
 * A chosen image is stored at once, so a slow or refused upload is reported
 * beside it, but the ARC uses it only once the draft is saved - like
 * everything else here, Apply puts it in the working copy. An ARC without
 * its own images shows the organization's profile images, so removing one
 * brings the profile's back. Closing with anything changed asks first.
 */
export function EditHeaderDialog({
  arc,
  uploadImage,
  onApply,
  onClose,
}: EditHeaderDialogProps) {
  const [intro, setIntro] = useState<JSONContent | null>(
    arc.header.intro_content ?? null,
  );
  // Compared with what the dialog opened on, not flagged on the first
  // keystroke: typing a letter and deleting it again is no change, and must
  // not ask "Leave without saving?" on Cancel.
  const introChanged = canonical(intro) !== canonical(arc.header.intro_content ?? null);
  const [logo, setLogo] = useState<PendingImage | null | undefined>(undefined);
  const [cover, setCover] = useState<PendingImage | null | undefined>(undefined);

  // Addresses of images put in the text in this dialog, by storage key.
  const stored = useRef<Record<string, string>>({});
  const [images] = useState<RichTextImages>(() => ({
    address: (key) => stored.current[key] ?? arc.header.intro_images?.[key] ?? null,
    upload: async (file) => {
      const image = await uploadImage('inline', file);
      if (image.url) stored.current[image.key] = image.url;
      return image;
    },
  }));

  const dirty = introChanged || logo !== undefined || cover !== undefined;

  const apply = () => {
    if (dirty) {
      onApply({
        ...(introChanged ? { intro: { content: intro, images: stored.current } } : {}),
        ...(logo !== undefined ? { logo } : {}),
        ...(cover !== undefined ? { cover } : {}),
      });
    }
    onClose();
  };

  /** The address the box shows: the image chosen here, else the ARC's own. */
  const shown = (chosen: PendingImage | null | undefined, kind: ImageKind) => {
    if (chosen !== undefined) return chosen?.url ?? null;
    const own = kind === 'logo' ? arc.header.logo : arc.header.cover_image;
    return own?.own ? own.url : null;
  };

  /** The line under an image box: whose image the app shows. */
  const fallback = (src: string | null, noun: string) =>
    src ? null : (
      <Typography variant="caption" color="text.secondary">
        Not set: the app shows the organization&apos;s profile {noun}.
      </Typography>
    );

  const logoSrc = shown(logo, 'logo');
  const coverSrc = shown(cover, 'cover');

  return (
    <FormDialog
      open
      title="Edit header"
      description="What app users see at the top of the ARC. Save the draft to keep your changes."
      dirty={dirty}
      pending={false}
      failure={null}
      submitLabel="Apply"
      onSubmit={(event) => {
        event.preventDefault();
        apply();
      }}
      onClose={onClose}
      maxWidth="md"
      testId="arc-builder-header-dialog"
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        }}
      >
        <Box>
          <ImageUpload
            label="ARC logo"
            alt="ARC logo"
            src={logoSrc}
            maxBytes={LOGO_MAX_BYTES}
            onUpload={async (file) => setLogo(await uploadImage('logo', file))}
            onRemove={() => setLogo(null)}
            testId="arc-builder-header-dialog-logo"
          />
          {fallback(logoSrc, 'logo')}
        </Box>
        <Box>
          <ImageUpload
            label="ARC cover image"
            alt="ARC cover image"
            src={coverSrc}
            maxBytes={COVER_MAX_BYTES}
            onUpload={async (file) => setCover(await uploadImage('cover', file))}
            onRemove={() => setCover(null)}
            testId="arc-builder-header-dialog-cover"
          />
          {fallback(coverSrc, 'cover image')}
        </Box>
      </Box>

      <RichTextField
        label="Introductory text"
        hint="Headings, bold, italic, lists, links, images and a call-to-action button."
        value={intro}
        onChange={setIntro}
        images={images}
        maxLength={arc.limits.rich_text.max_length}
        testId="arc-builder-header-dialog-intro"
      />
    </FormDialog>
  );
}
